/**
 * Sub commitment rollup from jobcost. Rendered behind JobcostErrorBoundary.
 */
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../../../api/client'

export type VendorCommitment = {
  vendor_id: number
  company_name: string
  trade: string | null
  committed_cents: number
  billed_cents: number
  paid_cents: number
  remaining_cents: number
  bid_amount_cents: number
}

function formatCents(cents: number): string {
  const sign = cents < 0 ? '-' : ''
  const abs = Math.abs(Math.trunc(cents))
  const dollars = Math.trunc(abs / 100)
  const frac = abs % 100
  return `${sign}$${dollars.toLocaleString('en-US')}.${String(frac).padStart(2, '0')}`
}

function Stat({ label, value, danger }: { label: string; value: number; danger?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink4)' }}>
        {label}
      </div>
      <div
        style={{
          marginTop: 4,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 14,
          fontWeight: 600,
          color: danger ? '#b91c1c' : 'var(--ink)',
        }}
      >
        {formatCents(value)}
      </div>
    </div>
  )
}

interface Props {
  projectId: number
  vendorId?: number | null
  companyName?: string | null
  enabled: boolean
}

export default function SubCommitmentCard({ projectId, vendorId, companyName, enabled }: Props) {
  const { data, isError, isPending } = useQuery({
    queryKey: ['jobcost-commitments', projectId],
    queryFn: () =>
      apiFetch<{ data: { vendors: VendorCommitment[] } }>('GET', `/api/jobcost/projects/${projectId}/commitments`),
    enabled,
    retry: false,
    staleTime: 30_000,
  })

  if (!enabled) return null
  if (isPending) {
    return <div style={{ fontSize: 12, color: 'var(--ink4)', marginTop: 8 }}>Loading commitment…</div>
  }
  if (isError) return null

  const vendors = data?.data?.vendors ?? []
  const row =
    vendors.find(v => vendorId != null && v.vendor_id === vendorId) ??
    vendors.find(v => companyName && v.company_name === companyName)
  if (!row) return null

  return (
    <div
      style={{
        marginTop: 8,
        padding: '10px 12px',
        border: '1px solid var(--line)',
        background: 'var(--surface)',
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
        <Stat label="Committed" value={row.committed_cents} />
        <Stat label="Billed" value={row.billed_cents} danger={row.billed_cents > row.committed_cents} />
        <Stat label="Paid" value={row.paid_cents} />
        <Stat label="Remaining" value={row.remaining_cents} danger={row.remaining_cents < 0} />
      </div>
    </div>
  )
}
